import _ from "lodash";
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import {
  IconStar,
  IconStarFilled,
  IconMessage,
  IconMessageOff,
  IconLogin2,
} from "@tabler/icons-react";
import Kudos from "./Kudos";
import TimeUtils from "utils/time";

export interface UserData {
  id?: string;
  name?: string;
  username?: {
    display?: string;
    standardized?: string;
  };
  above18?: boolean;
  messagesDisabled?: boolean;
  incognito?: boolean;
  streak?: number;
  streakInDanger?: boolean;
  active?: boolean;
  stats?: {
    duration?: number;
  };
}

export interface UserCardFooterProps {
  user: UserData;
  uid: string;
  isCurrentUser: boolean;
  isFavorite: boolean;
  handleFavoriteToggle: (e: React.MouseEvent) => void;
  currentUserAbove18: boolean;
  navigate: (path: string) => void;
  handleProfileClick: () => void;
  showNavigateToProfile?: boolean;
  showKudos?: boolean;
  showDuration?: boolean;
  onKudosSent?: (sent: boolean) => void;
}

const UserCardFooter = ({
  user,
  uid,
  isCurrentUser,
  isFavorite,
  handleFavoriteToggle,
  currentUserAbove18,
  navigate,
  handleProfileClick,
  showNavigateToProfile,
  showKudos = true,
  showDuration = true,
  onKudosSent,
}: UserCardFooterProps) => {
  // Format duration string
  const durationString =
    user.stats?.duration && showDuration
      ? TimeUtils.elapsedToString(
          TimeUtils.breakdown(user.stats.duration, true),
          1
        )
      : null;

  const contents = _.compact([
    // Favorite
    <Tooltip
      title={isFavorite ? "Remove from favorites" : "Add to favorites"}
      key="favorite"
    >
      <IconButton
        onClick={handleFavoriteToggle}
        sx={{
          color: isFavorite ? "#ffd700" : "grey.400",
          transition: "color 0.2s ease-in-out, transform 0.1s ease-in-out",
          "&:hover": {
            color: isFavorite ? "#ffb300" : "#ffd700",
            transform: "scale(1.1)",
          },
        }}
      >
        {isFavorite ? <IconStarFilled size={20} /> : <IconStar size={20} />}
      </IconButton>
    </Tooltip>,

    // Duration
    durationString ? (
      <Typography variant="body2" key="duration" sx={{ fontWeight: 500 }}>
        {durationString}
      </Typography>
    ) : null,

    // Chat
    <IconButton
      key="chat"
      onClick={() => navigate(`chats?uid=${uid}`)}
      disabled={!currentUserAbove18 || !user?.above18 || user?.incognito}
      color="primary"
    >
      {!currentUserAbove18 || !user?.above18 || user?.incognito ? (
        <IconMessageOff size={20} />
      ) : (
        <IconMessage size={20} />
      )}
    </IconButton>,
    showNavigateToProfile ? (
      <IconButton
        key="profile"
        onClick={(e) => {
          e.stopPropagation();
          handleProfileClick();
        }}
      >
        <IconLogin2 />
      </IconButton>
    ) : null,

    // Kudos - now always shown in footer when enabled
    showKudos ? (
      <Kudos
        key="encourage"
        user={user}
        isCurrentUser={isCurrentUser}
        currentUserAbove18={currentUserAbove18}
        onKudosSent={onKudosSent}
      />
    ) : null,
  ]);

  return (
    <Grid
      container
      style={{
        alignItems: "center",
        flexWrap: "nowrap",
        justifyContent: "space-around",
      }}
    >
      {_.map(contents, (item, index) => (
        <Grid item key={index}>
          {item}
        </Grid>
      ))}
    </Grid>
  );
};

export default UserCardFooter;
